import { defineStore } from 'pinia';
import {useUserStore, useCounterStore} from "@/console-new/stores";
import {useNotificationStore, useLanguageStore} from "@/shared/stores";
import API from "@/shared/helpers/API";
import {getLocalDateFromServer} from "@/shared/helpers/dates";
import {orderNumberDay} from "@/console-new/helpers/orders";
import {useSunmiStore} from "@/console-sun/stores/sunmi.store";
import EscPosReceipts from "@/console-sun/helpers/EscPosReceipts";

export const useCustomersStore = defineStore('customers', {
	state: () => ({
		customersPeople: [],
		customersPeopleTotal: 0,
		customersCompanies: [],
		customersCompaniesTotal: 0,
		personOrders: [],
		personOrdersTotal: 0,
		personPayments: [],
		personPaymentsTotal: 0,
		personCreditTopups: [],
		personCreditTopupsTotal: 0,
		companyOrders: [],
		companyOrdersTotal: 0,
		companyPayments: [],
		companyPaymentsTotal: 0,
		companyCreditTopups: [],
		companyCreditTopupsTotal: 0,
		companyPeople: [],
		companyPeopleTotal: 0,
	}),
	getters: {
		peoplesIds: state => {
			let finalIds = {};
			if(!state.customersPeople || !state.customersPeople.length) { return finalIds; }
			state.customersPeople.forEach(t => {
				finalIds[t.customer_id] = t;
			});
			return finalIds;
		},
		companiesIds: state => {
			let finalIds = {};
			if(!state.customersCompanies || !state.customersCompanies.length) { return finalIds; }
			state.customersCompanies.forEach(t => {
				finalIds[t.customer_id] = t;
			});
			return finalIds;
		}

	},
	actions: {
		getFilteredPeople(filters, callback = null) {

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); return; }

			API.init().getFilteredCustomersPeople(userStore.currBusinessId, JSON.stringify(filters || []), r => {
				this.customersPeople = [];
				this.customersPeopleTotal = 0;
				if(r && r.result === "OK") {
					if(r.customers && r.customers.selected && r.customers.selected.length) {
						r.customers.selected.forEach(t => {
							let normalized = this.normalizeCustomerPerson(t);
							if(!normalized.deleted) {
								this.customersPeople.push(normalized);
							}
						});
						this.customersPeopleTotal = Number(r.customers.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		normalizeCustomerPerson(customer) {
			customer.customer_id = customer.customer_id ? Number(customer.customer_id) : null;
			customer.address_id = customer.address_id ? Number(customer.address_id) : null;
			customer.fiscal_code = customer.fiscal_code || null;
			customer.name = customer.name || null;
			customer.birth_date = customer.birth_date ? new Date(customer.birth_date) : null;
			customer.phone = customer.phone || null;
			customer.email = customer.email || null;
			customer.gender = customer.gender || null;
			customer.notes = customer.notes || null;
			customer.token = customer.token || null;
			customer.credit = customer.credit ? Number(customer.credit) : 0;
			customer.debit = customer.debit ? Number(customer.debit) : 0;
			customer.deleted = Number(customer.deleted) === 1;
			return customer;
		},
		getFilteredCompanies(filters, callback = null) {

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); return; }

			API.init().getFilteredCustomersCompanies(userStore.currBusinessId, JSON.stringify(filters || []), r => {
				this.customersCompanies = [];
				this.customersCompaniesTotal = 0;
				if(r && r.result === "OK") {
					if(r.customers && r.customers.selected && r.customers.selected.length) {
						r.customers.selected.forEach(t => {
							let normalized = this.normalizeCustomerCompany(t);
							if(!normalized.deleted) {
								this.customersCompanies.push(normalized);
							}
						});
						this.customersCompaniesTotal = Number(r.customers.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		normalizeCustomerCompany(customer) {
			customer.customer_id = customer.customer_id ? Number(customer.customer_id) : null;
			customer.address_id = customer.address_id ? Number(customer.address_id) : null;
			customer.delivery_address_id = customer.delivery_address_id ? Number(customer.delivery_address_id) : null;
			customer.fiscal_code = customer.cf || null;
			customer.name = customer.name || null;
			customer.phone = customer.phone || null;
			customer.email = customer.email || null;
			customer.notes = customer.notes || null;
			customer.token = customer.token || null;
			customer.cf_fallback = customer.fiscal_code ? customer.fiscal_code : '-';
			customer.vat_fallback = customer.vat ? customer.vat : '-';
			customer.vat_cf_fallback = customer.vat ? customer.vat : customer.fiscal_code ? customer.fiscal_code : '-';
			customer.credit = customer.credit ? Number(customer.credit) : 0;
			customer.debit = customer.debit ? Number(customer.debit) : 0;
			customer.deleted = Number(customer.deleted) === 1;
			return customer;
		},
		parseCustomerToken(token, callbackSuccess, callbackError) {
			this.getCustomerFromToken(token, (success, customer, customerType) => {
				if(success && customer) {
					useCounterStore().selectedCustomer = {...customer, type: customerType};
					useNotificationStore().showNotification('success-customer-linked', [customer?.name || useLanguageStore().getString('customer')]);
					if(callbackSuccess) { callbackSuccess(customer); }
				} else {
					useNotificationStore().showNotification('warning-customer-missing');
					if(callbackError) { callbackError(); }
				}
			});
		},
		getCustomerFromToken(token, callback = null) {
			//callback: success, customer, customerType
			const userStore = useUserStore();
			if(!userStore?.currBusinessId
				|| !token || !token?.length || token.length !== 8
				|| !(token.startsWith('p') || token.startsWith('c'))
			) {
				callback && callback(false, null, null);
				return;
			}
			let customerType = token.startsWith('p') ? 'person' : 'company';
			let tokenWithoutPrefix = token.substring(1);
			let startingArray = customerType === 'person' ? this.customersPeople : this.customersCompanies;
			let normalizeFunc = customerType === 'person' ? this.normalizeCustomerPerson : this.normalizeCustomerCompany;
			let foundCustomer = (startingArray || []).find(customer => customer.token === tokenWithoutPrefix);
			if(foundCustomer) {
				callback && callback(true, foundCustomer, customerType);
				return;
			}

			API.init().getCustomerFromToken(userStore.currBusinessId, token, r => {
				if(r && r.result === "OK" && r.customer) {
					let normalized = normalizeFunc(r.customer);
					let targetArray = customerType === 'person' ? this.customersPeople : this.customersCompanies;
					//if a customer with same customer_id exists, update it
					let existingIndex = targetArray.findIndex(c => c.customer_id === normalized.customer_id);
					if(existingIndex > -1) {
						targetArray[existingIndex] = normalized;
					} else {
						targetArray.push(normalized);
					}
					callback && callback(true, normalized, customerType);
				} else {
					callback && callback(false, null, null);
				}
			});
		},
		getFilteredPersonOrdersAndPayments(customerId, filters, callback = null) {

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); return; }

			API.init().getFilteredPersonOrdersAndPayments(userStore.currBusinessId, customerId, JSON.stringify(filters || []), r => {
				this.personOrders = [];
				this.personOrdersTotal = 0;
				if(r && r.result === "OK") {
					if(r.orders_payments && r.orders_payments.selected && r.orders_payments.selected.length) {
						r.orders_payments.selected.forEach(t => {
							this.personOrders.push(this.normalizeCustomerOrderPayment(t));
						});
						this.personOrdersTotal = Number(r.orders_payments.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredPersonCreditTopups(customerId, filters, callback = null) {

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); return; }

			API.init().getFilteredPersonCreditTopups(userStore.currBusinessId, customerId, JSON.stringify(filters || []), r => {
				this.personCreditTopups = [];
				this.personCreditTopupsTotal = 0;
				if(r && r.result === "OK") {
					if(r.topups && r.topups.selected && r.topups.selected.length) {
						r.topups.selected.forEach(t => {
							this.personCreditTopups.push(this.normalizeCreditTopup(t));
						});
						this.personCreditTopupsTotal = Number(r.topups.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredPersonPayments(customerId, filters, callback = null) {

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); return; }

			API.init().getFilteredPersonPayments(userStore.currBusinessId, customerId, JSON.stringify(filters || []), r => {
				this.personPayments = [];
				this.personPaymentsTotal = 0;
				if(r && r.result === "OK") {
					if(r.payments && r.payments.selected && r.payments.selected.length) {
						r.payments.selected.forEach(t => {
							this.personPayments.push(this.normalizePayment(t));
						});
						this.personPaymentsTotal = Number(r.payments.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredCompanyPayments(customerId, filters, callback = null) {

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); return; }

			API.init().getFilteredCompanyPayments(userStore.currBusinessId, customerId, JSON.stringify(filters || []), r => {
				this.companyPayments = [];
				this.companyPaymentsTotal = 0;
				if(r && r.result === "OK") {
					if(r.payments && r.payments.selected && r.payments.selected.length) {
						r.payments.selected.forEach(t => {
							this.companyPayments.push(this.normalizePayment(t));
						});
						this.companyPaymentsTotal = Number(r.payments.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredCompanyCreditTopups(customerId, filters, callback = null) {

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); return; }

			API.init().getFilteredCompanyCreditTopups(userStore.currBusinessId, customerId, JSON.stringify(filters || []), r => {
				this.companyCreditTopups = [];
				this.companyCreditTopupsTotal = 0;
				if(r && r.result === "OK") {
					if(r.topups && r.topups.selected && r.topups.selected.length) {
						r.topups.selected.forEach(t => {
							this.companyCreditTopups.push(this.normalizeCreditTopup(t));
						});
						this.companyCreditTopupsTotal = Number(r.topups.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredCompanyOrdersAndPayments(customerId, filters, callback = null) {

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); return; }

			API.init().getFilteredCompanyOrdersAndPayments(userStore.currBusinessId, customerId, JSON.stringify(filters || []), r => {
				this.companyOrders = [];
				this.companyOrdersTotal = 0;
				if(r && r.result === "OK") {
					if(r.orders_payments && r.orders_payments.selected && r.orders_payments.selected.length) {
						r.orders_payments.selected.forEach(t => {
							this.companyOrders.push(this.normalizeCustomerOrderPayment(t));
						});
						this.companyOrdersTotal = Number(r.orders_payments.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		normalizeOrder(order) {
			order.date = order.date ? getLocalDateFromServer(order.date) : null;
			order.order_number = orderNumberDay(order);
			order.order_id = order.order_id ? Number(order.order_id) : null;
			order.total = order.total ? Number(order.total) : 0;
			return order;
		},
		normalizeCustomerOrderPayment(order) {
			order.date = order.date ? getLocalDateFromServer(order.date) : null;
			order.order_number = orderNumberDay(order);
			order.order_id = order.order_id ? Number(order.order_id) : null;
			order.payment_id = order.payment_id ? Number(order.payment_id) : null;
			order.amount = order.amount ? Number(order.amount) : 0;
			order.paid = Number(order?.paid || 0) === 1;
			return order;
		},
		normalizePayment(payment) {
			payment.date = payment.date ? getLocalDateFromServer(payment.date) : null;
			payment.payment_id = payment.payment_id ? Number(payment.payment_id) : null;
			payment.customer_company_id = payment.customer_company_id ? Number(payment.customer_company_id) : null;
			payment.customer_person_id = payment.customer_person_id ? Number(payment.customer_person_id) : null;
			payment.amount = payment.amount ? Number(payment.amount) : 0;
			return payment;
		},
		normalizeCreditTopup(topup) {
			topup.date = topup.date ? getLocalDateFromServer(topup.date) : null;
			topup.customer_balance_transaction_id = topup.customer_balance_transaction_id ? Number(topup.customer_balance_transaction_id) : null;
			topup.amount = topup.amount ? Number(topup.amount) : 0;
			topup.type = topup.type || null;
			return topup;
		},
		getFilteredCompanyPeople(customerId, filters, callback = null) {

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); return; }

			API.init().getFilteredCompanyPeople(userStore.currBusinessId, customerId, JSON.stringify(filters || []), r => {
				this.companyPeople = [];
				this.companyPeopleTotal = 0;
				if(r && r.result === "OK") {
					if(r.people && r.people.selected && r.people.selected.length) {
						r.people.selected.forEach(t => {
							this.companyPeople.push(this.normalizeCustomerPerson(t));
						});
						this.companyPeopleTotal = Number(r.people.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		localPrintCustomerCreditQr(customer, customerType, token) {
			const userStore = useUserStore();
			let customerData = Object.assign({}, customer, {type: customerType});
			if (token && (!customerData.token || customerData.token !== token)) { customerData.token = token; }
			let businessName = userStore.currBusiness?.name || "";
			let rawData = EscPosReceipts.init().customerCreditQr(customerData, businessName, userStore.currBusinessId);
			useSunmiStore().enqueuePrint(rawData);
		},
		printCustomerCreditQr(customerId, customerType, callback = null) {
			const userStore = useUserStore();
			if(!userStore?.currBusinessId || !customerId || !customerType) {
				callback && callback(false, "missing_params");
				return;
			}
			let customers = customerType === "company" ? this.customersCompanies : this.customersPeople;
			let customer = customers.find(c => c.customer_id === customerId);
			if(customer) {
				API.init().printCustomerCreditQr(userStore.currBusinessId, customerId, customerType, r => {
					let didLocalPrint = false;
					// token could be empty, we need to call api to be sure it is generated in case it was empty
					if(useSunmiStore().canLocalPrint && r?.customer?.token) {
						this.localPrintCustomerCreditQr(customer, customerType, r.customer.token);
						didLocalPrint = true;
					}
					if(r && r.result === "OK") {
						callback && callback(true);
					} else if(r?.details === "no_printers_found") {
						callback && callback(didLocalPrint, "no_printers_found");
					} else {
						callback && callback(didLocalPrint, r?.details || null);
					}
				});
			} else {
				API.init().printCustomerCreditQr(userStore.currBusinessId, customerId, customerType, r => {
					if(r && r.result === "OK") {
						callback && callback(true);
					} else {
						callback && callback(false, r?.details || null);
					}
				});

			}
		},
	}
});

